"use client";

import { useCallback, type CSSProperties, type MouseEvent } from "react";

export type GlowIntensity = "soft" | "medium" | "bold";

const ALPHA: Record<
  GlowIntensity,
  { border: number; borderMid: number; stroke: number; strokeMid: number; ambient: number }
> = {
  soft: { border: 0.25, borderMid: 0.12, stroke: 0.17, strokeMid: 0.07, ambient: 0.055 },
  medium: { border: 0.4, borderMid: 0.2, stroke: 0.25, strokeMid: 0.12, ambient: 0.085 },
  bold: { border: 0.5, borderMid: 0.25, stroke: 0.35, strokeMid: 0.15, ambient: 0.11 },
};

const MASK_STYLE: CSSProperties = {
  mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
  WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
  maskComposite: "exclude",
  WebkitMaskComposite: "xor",
};

function glowStyles(
  radius: number,
  ambientRadius: number,
  intensity: GlowIntensity
): {
  border: CSSProperties;
  stroke: CSSProperties;
  ambient: CSSProperties;
} {
  const a = ALPHA[intensity];
  return {
    stroke: {
      padding: "3px",
      background: `radial-gradient(${radius}px circle at var(--mx, 50%) var(--my, 50%), rgba(255,255,255,${a.stroke}), rgba(255,255,255,${a.strokeMid}) 35%, transparent 65%)`,
      filter: "blur(4px)",
      ...MASK_STYLE,
    },
    border: {
      padding: "1px",
      background: `radial-gradient(${radius}px circle at var(--mx, 50%) var(--my, 50%), rgba(255,255,255,${a.border}), rgba(255,255,255,${a.borderMid}) 35%, transparent 65%)`,
      ...MASK_STYLE,
    },
    ambient: {
      background: `radial-gradient(${ambientRadius}px circle at var(--mx, 50%) var(--my, 50%), rgba(255,255,255,${a.ambient}), transparent 50%)`,
      filter: "blur(24px)",
    },
  };
}

export function trackHoverGlow(e: MouseEvent<HTMLElement>) {
  const rect = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty("--mx", `${e.clientX - rect.left}px`);
  e.currentTarget.style.setProperty("--my", `${e.clientY - rect.top}px`);
}

export function useHoverGlow() {
  return useCallback((e: MouseEvent<HTMLElement>) => {
    trackHoverGlow(e);
  }, []);
}

export interface HoverGlowProps {
  radius?: number;
  ambientRadius?: number;
  intensity?: GlowIntensity;
  rounded?: string;
  outerRounded?: string;
  scaleOnHover?: boolean;
}

export function HoverGlow({
  radius = 500,
  ambientRadius = 550,
  intensity = "soft",
  rounded = "rounded-2xl",
  outerRounded,
  scaleOnHover = false,
}: HoverGlowProps) {
  const resolvedOuter = outerRounded ?? (
    rounded === "rounded-xl" ? "rounded-[16px]" : rounded === "rounded-2xl" ? "rounded-[24px]" : rounded
  );

  const styles = glowStyles(radius, ambientRadius, intensity);
  const scaleClass = scaleOnHover
    ? "transition-[opacity,transform] duration-500 ease-out group-hover:scale-[1.03]"
    : "transition-opacity duration-500";
  const ambientScaleClass = scaleOnHover
    ? "transition-[opacity,transform] duration-700 ease-out group-hover:scale-[1.03]"
    : "transition-opacity duration-700";

  return (
    <>
      <div
        className={`absolute inset-0 ${rounded} pointer-events-none opacity-0 group-hover:opacity-100 ${scaleClass}`}
        style={styles.stroke}
      />
      <div
        className={`absolute inset-0 ${rounded} pointer-events-none opacity-0 group-hover:opacity-100 ${scaleClass}`}
        style={styles.border}
      />
      <div
        className={`absolute -inset-3 ${resolvedOuter} pointer-events-none opacity-0 group-hover:opacity-100 ${ambientScaleClass}`}
        style={styles.ambient}
      />
    </>
  );
}

